package com.trevorwiebe.timesheet.admin.loadrate.presentation

import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRate
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRateCategory
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRateType
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRateUnit
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.displayName
import com.trevorwiebe.timesheet.core.domain.centsToDollarInput
import com.trevorwiebe.timesheet.core.presentation.common.DialogButtonRow
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import com.trevorwiebe.timesheet.mileage.presentation.LocationSearchDialog

@Composable
expect fun AddEditLoadRateDialog(
    editing: LoadRate?,
    locations: List<Location>,
    onDismiss: () -> Unit,
    onSave: (
        LoadRateCategory,
        LoadRateType,
        LoadRateUnit?,
        perUnitText: String,
        flatRateText: String,
        pickupLocationId: String,
        dropLocationId: String,
    ) -> Unit,
)

@Composable
internal fun AddEditLoadRateFormContent(
    editing: LoadRate?,
    locations: List<Location>,
    onDismiss: () -> Unit,
    onSave: (
        LoadRateCategory,
        LoadRateType,
        LoadRateUnit?,
        perUnitText: String,
        flatRateText: String,
        pickupLocationId: String,
        dropLocationId: String,
    ) -> Unit,
) {
    var category by remember { mutableStateOf(editing?.rateCategory ?: LoadRateCategory.BULK) }
    var type by remember { mutableStateOf(editing?.rateType ?: LoadRateType.FLAT_RATE) }
    var unit by remember { mutableStateOf(editing?.unit) }
    var perUnitText by remember {
        mutableStateOf(editing?.perUnit?.centsToDollarInput() ?: "")
    }
    var flatRateText by remember {
        mutableStateOf(editing?.flatRate?.centsToDollarInput() ?: "")
    }
    var pickupLocationId by remember { mutableStateOf(editing?.pickupLocationId ?: "") }
    var dropLocationId by remember { mutableStateOf(editing?.dropLocationId ?: "") }

    Column(
        modifier = Modifier
            .responsiveDialogWidth()
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = 16.dp),
    ) {
        Text(
            text = if (editing == null) "Add Load Rate" else "Edit Load Rate",
            fontSize = 20.sp,
            fontWeight = FontWeight.Bold
        )
        Spacer(modifier = Modifier.height(16.dp))

        RateLocationPickerRow(
            label = "Pickup location",
            locations = locations,
            selectedId = pickupLocationId,
            onSelect = { pickupLocationId = it }
        )
        Spacer(modifier = Modifier.height(8.dp))

        RateLocationPickerRow(
            label = "Drop location",
            locations = locations,
            selectedId = dropLocationId,
            onSelect = { dropLocationId = it }
        )
        Spacer(modifier = Modifier.height(8.dp))

        EnumPickerRow(
            label = "Category",
            selected = category.displayName,
            options = LoadRateCategory.entries.map { it.displayName },
            onSelect = { category = LoadRateCategory.entries[it] }
        )
        Spacer(modifier = Modifier.height(8.dp))

        EnumPickerRow(
            label = "Rate type",
            selected = type.displayName,
            options = LoadRateType.entries.map { it.displayName },
            onSelect = { type = LoadRateType.entries[it] }
        )
        Spacer(modifier = Modifier.height(8.dp))

        if (type == LoadRateType.FLAT_RATE) {
            TimesheetTextField(
                text = flatRateText,
                label = "Flat rate value",
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                onTextChange = { flatRateText = it }
            )
            Spacer(modifier = Modifier.height(8.dp))
        }

        if (type == LoadRateType.PER_UNIT) {
            EnumPickerRow(
                label = "Unit",
                selected = unit?.displayName ?: "Select a unit",
                options = LoadRateUnit.entries.map { it.displayName },
                onSelect = { unit = LoadRateUnit.entries[it] }
            )
            Spacer(modifier = Modifier.height(8.dp))

            TimesheetTextField(
                text = perUnitText,
                label = "Price per ${unit?.displayName ?: "unit"} ($)",
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                onTextChange = { perUnitText = it }
            )
            Spacer(modifier = Modifier.height(8.dp))
        }

        Spacer(modifier = Modifier.height(8.dp))

        DialogButtonRow(
            confirmButtonText = "Save",
            confirmEnabled = type == LoadRateType.FLAT_RATE || unit != null,
            onConfirmClick = {
                onSave(
                    category,
                    type,
                    unit,
                    perUnitText.trim(),
                    flatRateText.trim(),
                    pickupLocationId,
                    dropLocationId,
                )
            },
            dismissButtonText = "Cancel",
            onDismissClick = onDismiss
        )
        Spacer(modifier = Modifier.height(8.dp))
    }
}

/** Read-only row that opens the searchable location picker in select-only mode. */
@Composable
private fun RateLocationPickerRow(
    label: String,
    locations: List<Location>,
    selectedId: String,
    onSelect: (String) -> Unit,
) {
    var showPicker by remember { mutableStateOf(false) }
    val selected = locations.firstOrNull { it.id == selectedId }
    Column(modifier = Modifier.fillMaxWidth()) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Spacer(modifier = Modifier.height(4.dp))
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(4.dp))
                .border(1.dp, MaterialTheme.colorScheme.primary, RoundedCornerShape(4.dp))
                .clickable { showPicker = true }
                .padding(horizontal = 16.dp, vertical = 16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = selected?.let { "${it.name} — ${it.city}, ${it.state}" }
                    ?: "Select a location",
                style = MaterialTheme.typography.bodyLarge,
                color = if (selected == null) MaterialTheme.colorScheme.onSurfaceVariant
                else MaterialTheme.colorScheme.onBackground,
                modifier = Modifier.weight(1f)
            )
            Icon(
                imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }

    if (showPicker) {
        LocationSearchDialog(
            locations = locations,
            nameFieldLabel = "Location name",
            initialName = "",
            initialCity = "",
            initialState = "",
            onSelect = { onSelect(it.id); showPicker = false },
            onNewLocation = { _, _, _ -> },
            onDismiss = { showPicker = false },
            allowNewLocation = false,
        )
    }
}

/** Bordered picker row that drops down a fixed set of options. */
@Composable
internal fun EnumPickerRow(
    label: String,
    selected: String,
    options: List<String>,
    onSelect: (index: Int) -> Unit,
) {
    var expanded by remember { mutableStateOf(false) }
    Column(modifier = Modifier.fillMaxWidth()) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Spacer(modifier = Modifier.height(4.dp))
        Box {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(4.dp))
                    .border(1.dp, MaterialTheme.colorScheme.primary, RoundedCornerShape(4.dp))
                    .clickable { expanded = true }
                    .padding(horizontal = 16.dp, vertical = 16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = selected,
                    style = MaterialTheme.typography.bodyLarge,
                    modifier = Modifier.weight(1f)
                )
                Icon(
                    imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            DropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
                options.forEachIndexed { index, option ->
                    DropdownMenuItem(
                        text = { Text(option) },
                        onClick = { onSelect(index); expanded = false },
                    )
                }
            }
        }
    }
}
